/**
 * distributions.js
 * Statistical distribution library for reliability/availability simulation
 * Provides random variate generation and analytical functions
 */

/**
 * Seeded PRNG using Mulberry32 algorithm
 * Allows reproducible random sequences
 */
export class SeededRandom {
  constructor(seed = 1) {
    this.seed = seed;
  }

  random() {
    let x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }

  // Mulberry32 - faster, higher quality
  static mulberry32(seed) {
    return function() {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul((t ^ (t >>> 15)), (t | 1));
      t ^= t + Math.imul((t ^ (t >>> 7)), (t | 61));
      return (((t ^ (t >>> 14)) >>> 0)) / 4294967296;
    };
  }
}

/**
 * Get random number from current source
 */
function getRandom(rng) {
  if (rng && typeof rng.random === 'function') {
    return rng.random();
  }
  return Math.random();
}

/**
 * Weibull random variate via inverse CDF
 * Parameters: eta (scale), beta (shape), gamma (location, default 0)
 */
export function weibullRandom(eta, beta, gamma = 0, rng = null) {
  if (eta <= 0 || beta <= 0) {
    throw new Error('Weibull: eta and beta must be positive');
  }
  const u = getRandom(rng);
  return gamma + eta * Math.pow(-Math.log(u), 1 / beta);
}

/**
 * Exponential random variate
 * Parameter: lambda (rate parameter, 1/lambda = mean)
 */
export function exponentialRandom(lambda, rng = null) {
  if (lambda <= 0) {
    throw new Error('Exponential: lambda must be positive');
  }
  const u = getRandom(rng);
  return -Math.log(u) / lambda;
}

/**
 * Normal random variate via Box-Muller transform
 * Parameters: mu (mean), sigma (standard deviation)
 */
export function normalRandom(mu = 0, sigma = 1, rng = null) {
  if (sigma <= 0) {
    throw new Error('Normal: sigma must be positive');
  }
  const u1 = getRandom(rng);
  const u2 = getRandom(rng);
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mu + sigma * z;
}

/**
 * Lognormal random variate
 * Parameters: mu, sigma (of the underlying normal)
 */
export function lognormalRandom(mu, sigma, rng = null) {
  const z = normalRandom(mu, sigma, rng);
  return Math.exp(z);
}

/**
 * Gamma random variate using Marsaglia & Tsang method
 * Parameters: shape (alpha), scale (beta)
 */
export function gammaRandom(shape, scale = 1, rng = null) {
  if (shape <= 0 || scale <= 0) {
    throw new Error('Gamma: shape and scale must be positive');
  }

  // For shape < 1, use acceptance-rejection
  if (shape < 1) {
    const d = shape + 1 - 1 / 3;
    const c = 1 / Math.sqrt(9 * d);
    while (true) {
      const z = normalRandom(0, 1, rng);
      const v = 1 + c * z;
      if (v > 0) {
        const u = getRandom(rng);
        const logU = Math.log(u);
        if (logU < 0.5 * z * z + d * (1 - v + Math.log(v))) {
          return scale * d * v;
        }
      }
    }
  }

  // Marsaglia & Tsang for shape >= 1
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  while (true) {
    const z = normalRandom(0, 1, rng);
    const v = 1 + c * z;
    if (v > 0) {
      const u = getRandom(rng);
      const logU = Math.log(u);
      if (logU < 0.5 * z * z + d * (1 - v + Math.log(v))) {
        return scale * d * v;
      }
    }
  }
}

/**
 * Uniform random variate on [a, b]
 */
export function uniformRandom(a, b, rng = null) {
  if (a >= b) {
    throw new Error('Uniform: a must be less than b');
  }
  const u = getRandom(rng);
  return a + u * (b - a);
}

/**
 * Bi-Weibull mixture (weighted combination of two Weibull distributions)
 */
export function biWeibullRandom(eta1, beta1, eta2, beta2, mixingWeight = 0.5, rng = null) {
  if (mixingWeight < 0 || mixingWeight > 1) {
    throw new Error('Bi-Weibull: mixing weight must be in [0,1]');
  }
  const u = getRandom(rng);
  if (u < mixingWeight) {
    return weibullRandom(eta1, beta1, 0, rng);
  } else {
    return weibullRandom(eta2, beta2, 0, rng);
  }
}

/**
 * Fixed value (deterministic, no randomness)
 */
export function fixedValue(value) {
  return value;
}

/**
 * Generate failure time based on failure model object
 * Model can have: {distribution, eta, beta, gamma, lambda, mu, sigma, mttf, value}
 */
export function generateFailureTime(failureModel, rng = null) {
  if (!failureModel || !failureModel.distribution) {
    throw new Error('generateFailureTime: failureModel must have a distribution property');
  }

  const dist = failureModel.distribution.toLowerCase();

  switch (dist) {
    case 'weibull':
      return weibullRandom(
        failureModel.eta || 1,
        failureModel.beta || 1,
        failureModel.gamma || 0,
        rng
      );
    case 'exponential':
      // If MTTF is given, lambda = 1/MTTF
      const lambda = failureModel.lambda || (1 / (failureModel.mttf || 1));
      return exponentialRandom(lambda, rng);
    case 'normal':
      return Math.max(0, normalRandom(failureModel.mu || 1000, failureModel.sigma || 100, rng));
    case 'lognormal':
      return lognormalRandom(failureModel.mu || 6, failureModel.sigma || 0.5, rng);
    case 'gamma':
      return gammaRandom(failureModel.shape || 2, failureModel.scale || 100, rng);
    case 'uniform':
      return uniformRandom(failureModel.min || 0, failureModel.max || 1000, rng);
    case 'fixed':
      return fixedValue(failureModel.value || 0);
    case 'biweibull':
    case 'bi-weibull':
    case 'mixed weibull': {
      // Competing risk model: min of two Weibull failure modes
      const t1 = weibullRandom(failureModel.eta || 1, failureModel.beta || 1, failureModel.gamma || 0, rng);
      const t2 = weibullRandom(
        failureModel.eta2 || failureModel.eta * 1.5 || 1,
        failureModel.beta2 || Math.max(0.5, (failureModel.beta || 1) * 0.6),
        failureModel.gamma2 || 0,
        rng
      );
      return Math.min(t1, t2);
    }
    default:
      // Fallback: treat as exponential with MTTF if available
      console.warn(`Unknown distribution "${dist}", falling back to exponential`);
      const fallbackLambda = failureModel.lambda || (1 / (failureModel.mttf || failureModel.eta || 5000));
      return exponentialRandom(fallbackLambda, rng);
  }
}

/**
 * Generate repair time based on repair model object
 * Same structure as failure model
 */
export function generateRepairTime(repairModel, rng = null) {
  if (!repairModel || !repairModel.distribution) {
    throw new Error('generateRepairTime: repairModel must have a distribution property');
  }

  const dist = repairModel.distribution.toLowerCase();

  switch (dist) {
    case 'weibull':
      return weibullRandom(
        repairModel.eta || 1,
        repairModel.beta || 1,
        repairModel.gamma || 0,
        rng
      );
    case 'exponential':
      const lambda = repairModel.lambda || (1 / (repairModel.mttr || 1));
      return exponentialRandom(lambda, rng);
    case 'normal':
      return Math.max(0, normalRandom(repairModel.mu || 10, repairModel.sigma || 2, rng));
    case 'lognormal':
      return lognormalRandom(repairModel.mu || 2, repairModel.sigma || 0.5, rng);
    case 'gamma':
      return gammaRandom(repairModel.shape || 2, repairModel.scale || 5, rng);
    case 'uniform':
      return uniformRandom(repairModel.min || 0, repairModel.max || 20, rng);
    case 'fixed':
      return fixedValue(repairModel.value || 1);
    default:
      // Fallback: use MTTR as fixed repair time
      console.warn(`Unknown repair distribution "${dist}", falling back to fixed MTTR`);
      return fixedValue(repairModel.value || repairModel.mttr || 8);
  }
}

// ============================================================================
// Analytical Functions (PDF, CDF, Reliability)
// ============================================================================

/**
 * Weibull PDF
 */
export function weibullPDF(t, eta, beta, gamma = 0) {
  if (t <= gamma) return 0;
  const x = (t - gamma) / eta;
  return (beta / eta) * Math.pow(x, beta - 1) * Math.exp(-Math.pow(x, beta));
}

/**
 * Weibull CDF
 */
export function weibullCDF(t, eta, beta, gamma = 0) {
  if (t <= gamma) return 0;
  const x = (t - gamma) / eta;
  return 1 - Math.exp(-Math.pow(x, beta));
}

/**
 * Weibull reliability function R(t) = 1 - F(t)
 */
export function weibullReliability(t, eta, beta, gamma = 0) {
  if (t <= gamma) return 1;
  const x = (t - gamma) / eta;
  return Math.exp(-Math.pow(x, beta));
}

/**
 * Exponential PDF
 */
export function exponentialPDF(t, lambda) {
  if (t < 0) return 0;
  return lambda * Math.exp(-lambda * t);
}

/**
 * Exponential CDF
 */
export function exponentialCDF(t, lambda) {
  if (t < 0) return 0;
  return 1 - Math.exp(-lambda * t);
}

/**
 * Exponential reliability function
 */
export function exponentialReliability(t, lambda) {
  if (t < 0) return 1;
  return Math.exp(-lambda * t);
}

/**
 * Normal PDF
 */
export function normalPDF(x, mu = 0, sigma = 1) {
  if (sigma <= 0) return 0;
  const z = (x - mu) / sigma;
  return (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
}

/**
 * Normal CDF using error function approximation
 */
export function normalCDF(x, mu = 0, sigma = 1) {
  const z = (x - mu) / sigma;
  // Abramowitz and Stegun approximation
  const p = 0.2316419;
  const b1 = 0.319381530;
  const b2 = -0.356563782;
  const b3 = 1.781477937;
  const b4 = -1.821255978;
  const b5 = 1.330274429;

  const t = 1.0 / (1.0 + p * Math.abs(z));
  const pdf = normalPDF(z, 0, 1);
  const cdf = 1 - pdf * (b1 * t + b2 * t * t + b3 * t * t * t + b4 * t * t * t * t + b5 * t * t * t * t * t);
  return z >= 0 ? cdf : 1 - cdf;
}

/**
 * Lognormal PDF
 */
export function lognormalPDF(x, mu, sigma) {
  if (x <= 0 || sigma <= 0) return 0;
  const lnx = Math.log(x);
  const z = (lnx - mu) / sigma;
  return (1 / (x * sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
}

/**
 * Lognormal CDF
 */
export function lognormalCDF(x, mu, sigma) {
  if (x <= 0) return 0;
  return normalCDF(Math.log(x), mu, sigma);
}

/**
 * Gamma function using Stirling's approximation for large values
 * and series expansion for small values
 */
function logGamma(x) {
  if (x < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * x)) - logGamma(1 - x);
  }
  x -= 1;
  const coef = [
    676.5203681218851,
    -1259.1392167224028,
    771.32342877765313,
    -176.61502916214059,
    12.507343278686905,
    -0.13857109526572012,
    9.9843695780195716e-6,
    1.5056327351493116e-7
  ];
  let base = x + 7 + 0.5;
  let sum = 0.99999999999980993;
  for (let i = 0; i < coef.length; i++) {
    sum += coef[i] / (x + i + 1);
  }
  return Math.log(Math.sqrt(2 * Math.PI)) + (x + 0.5) * Math.log(base) - base + Math.log(sum);
}

/**
 * Incomplete gamma function (regularized)
 */
function lowerGamma(a, x) {
  if (x < 0 || a <= 0) return 0;
  if (x === 0) return 0;
  if (x >= a + 1) {
    return 1 - upperGamma(a, x);
  }
  let sum = 1 / a;
  let term = 1 / a;
  for (let i = 1; i < 100; i++) {
    term *= x / (a + i);
    sum += term;
    if (Math.abs(term) < 1e-10) break;
  }
  return Math.exp(-x + a * Math.log(x) - logGamma(a)) * sum;
}

function upperGamma(a, x) {
  if (x < 0 || a <= 0) return 1;
  if (x === 0) return 1;
  // Continued fraction approximation
  const maxIter = 100;
  let result = 0;
  let am = 1;
  let bm = x + 1 - a;
  let az = 1;
  let _qab = a * (x + 1 - a);
  let qam = a;
  let qap = a + 1;

  for (let i = 1; i <= maxIter; i++) {
    const d = (i * (a - i) * bm) / (qam * (qam + 1));
    am = 1 + d * am;
    bm = bm + 2 - d;
    const d2 = ((x + i) * (a + i - x) * bm) / (qap * (qap + 1));
    az *= 1 + d2;
    qam += 2;
    qap += 2;
    result = Math.abs(az - result) > 1e-10 ? az : result;
  }
  return Math.exp(-x + a * Math.log(x) - logGamma(a)) * az;
}

/**
 * Gamma CDF
 */
export function gammaCDF(x, shape, scale = 1) {
  if (x <= 0) return 0;
  return lowerGamma(shape, x / scale);
}

const distributionsExport = {
  SeededRandom,
  weibullRandom,
  exponentialRandom,
  normalRandom,
  lognormalRandom,
  gammaRandom,
  uniformRandom,
  biWeibullRandom,
  fixedValue,
  generateFailureTime,
  generateRepairTime,
  weibullPDF,
  weibullCDF,
  weibullReliability,
  exponentialPDF,
  exponentialCDF,
  exponentialReliability,
  normalPDF,
  normalCDF,
  lognormalPDF,
  lognormalCDF,
  gammaCDF,
};

export default distributionsExport;
